/**
 * Mock Image Canvas Renderer
 *
 * Web component for rendering artwork with perspective transformation onto a mock frame.
 * Uses multi-tile subdivision for accurate perspective rendering.
 */

// Quality presets: subdivisions for multi-tile rendering
const QUALITY_PRESETS = {
  low: 15,
  medium: 30,
  high: 50,
  ultra: 80
};

class MockImageCanvas extends HTMLElement {
  constructor() {
    super();
    this.canvas = null;
    this.ctx = null;
    this.artworkImg = null;
    this.frameImg = null;
    this.overlayImg = null;
    this.resizeObserver = null;
    this.isRendering = false;
  }

  connectedCallback() {
    // Create canvas element
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d', { alpha: true });
    this.appendChild(this.canvas);

    // Load images and render
    this.loadAndRender();

    // Set up resize observer for responsive rendering
    // Skip ResizeObserver if disable_resize_observer attribute is present (for fixed-size contexts like thumbnails)
    const shouldDisableObserver = this.hasAttribute('data-disable-resize-observer') ||
                                   this.dataset.disableResizeObserver === 'true';

    if (!shouldDisableObserver) {
      // Debounce render calls to avoid excessive redraws during interactions
      let resizeTimeout;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isRendering) return;

        // Clear pending render
        if (resizeTimeout) {
          cancelAnimationFrame(resizeTimeout);
        }

        // Debounce to next frame
        resizeTimeout = requestAnimationFrame(() => {
          this.render();
        });
      });
      this.resizeObserver.observe(this);
    }

    // Listen for artwork changes (when user uploads a photo)
    const container = this.closest('.mock-image-container');
    if (container) {
      this.artworkChangeHandler = () => this.handleArtworkChange();
      container.addEventListener('artwork-changed', this.artworkChangeHandler);
    }
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    const container = this.closest('.mock-image-container');
    if (container && this.artworkChangeHandler) {
      container.removeEventListener('artwork-changed', this.artworkChangeHandler);
    }
  }

  /**
   * Handle artwork change event (when user uploads a photo)
   */
  async handleArtworkChange() {
    const container = this.closest('.mock-image-container');
    if (!container) return;

    const artworkSrc = container.dataset.artworkSrc;
    if (artworkSrc) {
      // Reload artwork image and re-render
      try {
        this.artworkImg = await this.loadImage(artworkSrc);
        this.render();
      } catch (error) {
        console.warn('[MockImageCanvas] Failed to load updated artwork:', error);
      }
    }
  }

  async loadAndRender() {
    try {
      // Check for artwork override from container (uploaded photo)
      const container = this.closest('.mock-image-container');
      const artworkOverride = container?.dataset.artworkSrc;

      // Parse data attributes
      const artworkSrc = artworkOverride || this.dataset.artworkSrc;
      const artworkSrcset = this.dataset.artworkSrcset;
      const frameSrc = this.dataset.frameSrc;
      const frameSrcset = this.dataset.frameSrcset;
      const overlaySrc = this.dataset.overlaySrc;
      const overlaySrcset = this.dataset.overlaySrcset;
      const sizes = this.dataset.sizes;
      const cornersData = this.dataset.corners;

      if (!artworkSrc || !cornersData) {
        return;
      }

      // Parse corners JSON
      try {
        this.corners = JSON.parse(cornersData);
        if (!Array.isArray(this.corners) || this.corners.length !== 4) {
          this.renderFallback();
          return;
        }
      } catch (e) {
        this.renderFallback();
        return;
      }

      // Load artwork image with responsive source selection
      const artworkImageSrc = this.selectResponsiveImage(artworkSrc, artworkSrcset, sizes);
      this.artworkImg = await this.loadImage(artworkImageSrc);

      // Load frame image (optional but recommended)
      if (frameSrc) {
        const frameImageSrc = this.selectResponsiveImage(frameSrc, frameSrcset, sizes);
        this.frameImg = await this.loadImage(frameImageSrc);
      }

      // Load overlay image (optional)
      if (overlaySrc) {
        const overlayImageSrc = this.selectResponsiveImage(overlaySrc, overlaySrcset, sizes);
        this.overlayImg = await this.loadImage(overlayImageSrc);
      }

      // Initial render
      this.render();
    } catch (error) {
      // Silently fail
    }
  }

  /**
   * Select the best image source from srcset based on container size
   * @param {string} defaultSrc - Fallback image URL
   * @param {string} srcset - Srcset string from data attribute
   * @param {string} sizes - Sizes attribute (optional)
   * @returns {string} - Selected image URL
   */
  selectResponsiveImage(defaultSrc, srcset, sizes) {
    if (!srcset) {
      return defaultSrc;
    }

    // Parse srcset into array of {url, width} objects
    const sources = srcset.split(',').map(source => {
      const parts = source.trim().split(' ');
      return {
        url: parts[0],
        width: parseInt(parts[1], 10) // Remove 'w' suffix
      };
    }).filter(source => !isNaN(source.width));

    if (sources.length === 0) {
      return defaultSrc;
    }

    // Get container width (or use viewport width as fallback)
    const rect = this.getBoundingClientRect();
    const containerWidth = rect.width || window.innerWidth;

    // Account for device pixel ratio
    const dpr = window.devicePixelRatio || 1;
    const targetWidth = containerWidth * dpr;

    // Find the smallest image that's still larger than target width
    // This matches browser's srcset selection logic
    let selectedSource = sources[sources.length - 1]; // Default to largest
    for (const source of sources) {
      if (source.width >= targetWidth) {
        selectedSource = source;
        break;
      }
    }

    return selectedSource.url;
  }

  loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      // Don't set crossOrigin for same-origin images (Shopify CDN)
      // img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
      img.src = src;
    });
  }

  render() {
    if (!this.artworkImg || !this.ctx || this.isRendering) {
      return;
    }

    this.isRendering = true;

    try {
      // Get container dimensions
      const rect = this.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      if (width === 0 || height === 0) {
        this.isRendering = false;
        return;
      }

      // Set up high-DPI rendering
      const dpr = window.devicePixelRatio || 1;
      this.canvas.width = width * dpr;
      this.canvas.height = height * dpr;
      this.canvas.style.width = width + 'px';
      this.canvas.style.height = height + 'px';
      this.ctx.scale(dpr, dpr);

      // Clear canvas
      this.ctx.clearRect(0, 0, width, height);

      // Get quality setting
      const quality = this.dataset.quality || 'medium';
      const subdivisions = QUALITY_PRESETS[quality] || QUALITY_PRESETS.medium;

      // Render artwork with perspective
      this.drawPerspectiveImage(this.artworkImg, this.corners, width, height, subdivisions);

      // Render overlay if present
      if (this.overlayImg) {
        this.drawPerspectiveImage(this.overlayImg, this.corners, width, height, subdivisions);
      }

      // Render frame on top if present
      if (this.frameImg) {
        this.ctx.save();
        this.ctx.imageSmoothingEnabled = true;
        this.ctx.imageSmoothingQuality = 'high';
        this.ctx.drawImage(this.frameImg, 0, 0, width, height);
        this.ctx.restore();
      }
    } finally {
      this.isRendering = false;
    }
  }

  /**
   * Draw image with perspective transformation using multi-tile subdivision
   * Based on the provided algorithm for accurate perspective rendering
   */
  drawPerspectiveImage(img, corners, canvasWidth, canvasHeight, subdivisions) {
    // Convert normalized corners to pixel coordinates
    const pixelCorners = corners.map(c => ({
      x: c.x * canvasWidth,
      y: c.y * canvasHeight
    }));

    this.ctx.save();
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';

    // Multi-tile rendering for better perspective accuracy
    for (let row = 0; row < subdivisions; row++) {
      for (let col = 0; col < subdivisions; col++) {
        const u0 = col / subdivisions;
        const u1 = (col + 1) / subdivisions;
        const v0 = row / subdivisions;
        const v1 = (row + 1) / subdivisions;

        // Bilinear interpolation for quad corners
        const getPoint = (u, v) => {
          // Interpolate top edge
          const top = {
            x: pixelCorners[0].x * (1 - u) + pixelCorners[1].x * u,
            y: pixelCorners[0].y * (1 - u) + pixelCorners[1].y * u
          };
          // Interpolate bottom edge
          const bottom = {
            x: pixelCorners[3].x * (1 - u) + pixelCorners[2].x * u,
            y: pixelCorners[3].y * (1 - u) + pixelCorners[2].y * u
          };
          // Interpolate between top and bottom
          return {
            x: top.x * (1 - v) + bottom.x * v,
            y: top.y * (1 - v) + bottom.y * v
          };
        };

        const p0 = getPoint(u0, v0); // Top-left
        const p1 = getPoint(u1, v0); // Top-right
        const p3 = getPoint(u0, v1); // Bottom-left

        // Source coordinates with slight overlap to prevent seams
        const overlap = 0.5;
        const sx = Math.max(0, u0 * img.width - overlap);
        const sy = Math.max(0, v0 * img.height - overlap);
        const sw = Math.min(img.width - sx, (u1 - u0) * img.width + overlap * 2);
        const sh = Math.min(img.height - sy, (v1 - v0) * img.height + overlap * 2);

        // Calculate transformation vectors
        const dx1 = p1.x - p0.x; // Horizontal vector
        const dy1 = p1.y - p0.y;
        const dx2 = p3.x - p0.x; // Vertical vector
        const dy2 = p3.y - p0.y;

        const sourceWidth = (u1 - u0) * img.width;
        const sourceHeight = (v1 - v0) * img.height;

        // Apply transformation and draw tile
        this.ctx.save();
        this.ctx.transform(
          dx1 / sourceWidth,  // Horizontal scale/skew
          dy1 / sourceWidth,
          dx2 / sourceHeight, // Vertical scale/skew
          dy2 / sourceHeight,
          p0.x,               // Translation X
          p0.y                // Translation Y
        );

        this.ctx.drawImage(
          img,
          sx, sy, sw, sh,
          -overlap, -overlap,
          sourceWidth + overlap * 2,
          sourceHeight + overlap * 2
        );

        this.ctx.restore();
      }
    }

    this.ctx.restore();
  }
}

// Register the custom element
if (!customElements.get('mock-image-canvas')) {
  customElements.define('mock-image-canvas', MockImageCanvas);
}
